/**
 * Détail d'un dossier (F-CRM-03 à 05). Trois lectures du même dossier selon le rôle :
 * formateur et admin voient tout ; l'apprenant ne voit que son espace, ses questionnaires, son émargement, ses supports.
 * Les boutons d'action viennent de `d.actions` : c'est le noyau qui dit qui peut faire quoi, et pourquoi c'est bloqué.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BellRing, Check, ChevronRight, Download, FileX2, Lock, MailPlus, PenLine, Plus, Trash2, Unlock } from "lucide-react";
import { api, ErreurApi, dateFr, dateLongue, euros, heuresFr, instantFr, octets, type CoffreApprenant, type Dossier, type SeanceEmargement } from "../api";
import { suiviEnDirect } from "../requetes";
import { useActeur } from "../session";
import { Alerte, Bouton, Carte, Champ, Chargement, cx, useTitreDocument, DepotFichier, Etiquette, ListeManques, Modale, Onglets, Selecteur, ZoneTexte, useNotifier } from "../ui/base";
import { ChampSiret } from "../ui/ChampSiret";
import { ListeOuAutre } from "../ui/champs";
import { FINANCEURS, MODALITES as LISTE_MODALITES, NIVEAUX } from "@/domaine/pedagogie/listes";
import { avecOpco, choixModes, libelleMode } from "@/domaine/financements/catalogue";
import { CarteFinancementsDossier } from "./Financements";
import { ZoneDeTrace } from "../ui/Signature";
import { ASSIDUITES } from "@/domaine/dossier/cloture";
import { TransmissionCpf } from "./Cpf";
import { AutresPieces, EspaceCommunication, ListePieces, useRafraichirDossier } from "./DossierPieces";
import { FormulairesApprenant, Questionnaires } from "./DossierQuestionnaires";
import { Avatar } from "../ui/Avatar";
import { AccueilDuDossier, EnTeteApprenant, PlanApprenant } from "./EspaceApprenant";

const ETAPES = ["A", "B", "C", "D", "E", "F", "G"] as const;
const CHAMP_SEANCE = "h-10 min-w-0 rounded-sm border border-trait-fort bg-carte px-2 text-sm hover:border-encre-3 focus:border-accent focus:ring-[3px] focus:ring-accent/40 focus:outline-none";
const TITRES_ETAPES = ["Création", "Financement", "Début", "Fin", "Paiement", "Encaissé", "Archivé"];
// 04/11/2026 : libellés communs ; un mode retiré s'affiche « (ancien mode) ».
const libelleFinancement = (m: string) => libelleMode(m);
const MODALITES: Record<string, string> = { presentiel: "Présentiel", distanciel: "Distanciel", mixte: "Mixte" };

export function EcranDossier() {
  const { id } = useParams({ from: "/app/dossiers/$id" });
  const acteur = useActeur();
  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => api.get<Dossier>(`/dossiers/${id}`), ...suiviEnDirect });
  if (dossier.isPending) return <Chargement />;
  if (dossier.error) return <Alerte ton="danger">{dossier.error.message}</Alerte>;
  return acteur.role === "apprenant" ? <VueApprenant d={dossier.data} /> : <VueInterne d={dossier.data} />;
}

function Frise({ d }: { d: Dossier }) {
  const rang = ETAPES.indexOf(d.etape as (typeof ETAPES)[number]);
  const refuse = d.sous_statut === "refus_financement";
  return (
    <ol className="mt-5 grid grid-cols-7 gap-1 sm:gap-1.5" aria-label="Avancement du dossier">
      {ETAPES.map((e, i) => (
        <li key={e} aria-current={i === rang ? "step" : undefined}>
          <span className={cx("block h-1.5 rounded-full", i < rang ? "bg-valide" : i === rang ? (refuse ? "bg-danger" : "bg-attente") : "bg-papier-3")} />
          <span className={cx("mt-1.5 block truncate text-[11px] font-medium", i === rang ? "text-encre" : "text-encre-3")}>{TITRES_ETAPES[i]}</span>
        </li>
      ))}
    </ol>
  );
}

function EnTete({ d, children }: { d: Dossier; children?: React.ReactNode }) {
  const acteur = useActeur();
  useTitreDocument(`${d.dossier_reference} · ${d.formation.formation_titre}`);
  return (
    <header className="mb-6">
      <Link to={acteur.role === "formateur" ? "/dossiers" : "/"} className="mb-3 inline-flex items-center gap-1.5 text-sm text-encre-3 hover:text-encre">
        <ArrowLeft className="size-4" aria-hidden /> Retour
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="chiffres flex flex-wrap items-center gap-2 text-[13px] font-semibold tracking-wide text-encre-3">
            {d.dossier_reference}
            <Etiquette ton={d.sous_statut === "refus_financement" ? "danger" : d.archive ? "neutre" : "accent"}>{d.libelle_statut}</Etiquette>
            {d.archive && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-encre-3">
                <Lock className="size-3" aria-hidden /> Lecture seule
              </span>
            )}
          </p>
          <h1 className="mt-1.5 text-[24px] leading-tight font-semibold sm:text-[28px]">{d.formation.formation_titre}</h1>
          <p className="chiffres mt-1 text-encre-2">
            {d.entreprise.entreprise_nom} · {d.formation.formation_date_debut ? `du ${dateFr(d.formation.formation_date_debut)} au ${dateFr(d.formation.formation_date_fin)}` : "dates à définir"}
          </p>
        </div>
        {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
      </div>
      <Frise d={d} />
    </header>
  );
}

// ——— Actions du pipeline ———
function Actions({ d }: { d: Dossier }) {
  const rafraichir = useRafraichirDossier(d.id);
  const notifier = useNotifier();
  const [avecMotif, setAvecMotif] = useState<Dossier["actions"][number] | null>(null);
  const [refus, setRefus] = useState(false);
  const [motif, setMotif] = useState("");
  const [manques, setManques] = useState<string[]>([]);

  const executer = useMutation({
    mutationFn: (v: { action: string; motif?: string }) => api.post(`/dossiers/${d.id}/actions/${v.action}`, { motif: v.motif }),
    onSuccess: async () => {
      await rafraichir();
      setAvecMotif(null);
      setRefus(false);
      setMotif("");
      setManques([]);
    },
    onError: (e: Error & { details?: { manques?: string[] } | null }) => {
      setManques(e.details?.manques ?? []);
      notifier("danger", e.message);
    },
  });
  const deposerRefus = useMutation({ mutationFn: (f: File) => api.fichier(`/dossiers/${d.id}/pieces-externes/REF`, f), onSuccess: rafraichir, onError: (e) => notifier("danger", e.message) });

  const visibles = d.actions.filter((a) => a.action !== "enregistrer_refus");
  const refusPossible = d.actions.find((a) => a.action === "enregistrer_refus");
  const justificatifDepose = d.pieces.some((p) => p.code === "REF" && p.statut === "valide");
  if (visibles.length === 0 && !refusPossible) return null;

  return (
    <>
      {visibles.map((a, i) => (
        <Bouton
          key={a.action}
          variante={a.action === "renvoyer_en_brouillon" ? "secondaire" : i === visibles.length - 1 || visibles.length === 1 ? "primaire" : "secondaire"}
          disabled={a.bloqueePar !== null}
          title={a.bloqueePar ?? undefined}
          enCours={executer.isPending && executer.variables?.action === a.action}
          onClick={() => (a.motifRequis ? setAvecMotif(a) : executer.mutate({ action: a.action }))}
        >
          {a.libelle}
        </Bouton>
      ))}
      {refusPossible && (
        <Bouton variante="discret" icone={<FileX2 className="size-4" aria-hidden />} onClick={() => setRefus(true)}>
          Refus de financement
        </Bouton>
      )}
      {/* Un bouton grisé dit pourquoi, en clair et juste sous lui : l'info-bulle seule est invisible au clavier et sur téléphone. */}
      {visibles.some((a) => a.bloqueePar !== null) && (
        <div className="basis-full sm:max-w-xl sm:self-end">
          <Alerte ton="attention" titre="Pourquoi ce bouton est grisé">
            <ul className="space-y-0.5">
              {visibles
                .filter((a) => a.bloqueePar !== null)
                .map((a) => (
                  <li key={`raison-${a.action}`}>
                    <span className="font-medium">{a.libelle}</span> : {a.bloqueePar}
                  </li>
                ))}
            </ul>
          </Alerte>
        </div>
      )}
      {manques.length > 0 && (
        <div className="basis-full">
          <ListeManques titre="Il manque encore :" manques={manques} />
        </div>
      )}

      <Modale ouverte={avecMotif !== null} fermer={() => setAvecMotif(null)} titre={avecMotif?.libelle ?? ""}>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); executer.mutate({ action: avecMotif!.action, motif }); }}>
          <ZoneTexte libelle="Motif" required value={motif} onChange={(e) => setMotif(e.target.value)} aide="Le formateur le recevra par e-mail et le verra sur le dossier." />
          <Bouton type="submit" variante="primaire" enCours={executer.isPending}>
            Confirmer
          </Bouton>
        </form>
      </Modale>

      <Modale ouverte={refus} fermer={() => setRefus(false)} titre="Enregistrer un refus de financement">
        <div className="space-y-4">
          <Alerte ton="attention" titre="Le dossier sera archivé">
            Il restera consultable dans le pipeline, avec ses pièces, mais ne pourra plus être repris. Vous pourrez créer un nouveau dossier à partir de celui-ci.
          </Alerte>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-trait p-3">
            <p className="text-sm">{justificatifDepose ? "Justificatif du refus déposé." : "1. Déposez le justificatif du refus (courrier du financeur)."}</p>
            {justificatifDepose ? <Check className="size-5 text-valide" aria-hidden /> : <DepotFichier compact libelle="Déposer" enCours={deposerRefus.isPending} deposer={(f) => deposerRefus.mutate(f)} />}
          </div>
          <ZoneTexte libelle="2. Motif du refus (facultatif)" value={motif} onChange={(e) => setMotif(e.target.value)} />
          <Bouton variante="danger" disabled={!justificatifDepose} enCours={executer.isPending} onClick={() => executer.mutate({ action: "enregistrer_refus", motif })}>
            Enregistrer le refus et archiver
          </Bouton>
        </div>
      </Modale>
    </>
  );
}

// ——— Vue du formateur et de l'admin ———
type OngletInterne = "synthese" | "apprenant" | "of" | "emargement" | "journal";

function VueInterne({ d }: { d: Dossier }) {
  const acteur = useActeur();
  const navigate = useNavigate();
  const requetes = useQueryClient();
  const notifier = useNotifier();
  // 04/11/2026 : `?onglet=…&section=…` (éléments manquants des cartes du pipeline) ouvre le bon onglet, la bonne section.
  const cible = useSearch({ from: "/app/dossiers/$id" });
  const [onglet, setOnglet] = useState<OngletInterne>(() => (["synthese", "apprenant", "of", "emargement", "journal"].includes(cible.onglet ?? "") ? (cible.onglet as OngletInterne) : "synthese"));
  useEffect(() => {
    if (!cible.section) return;
    const t = window.setTimeout(() => document.getElementById(`section-${cible.section}`)?.scrollIntoView({ block: "start", behavior: "smooth" }), 50);
    return () => window.clearTimeout(t);
  }, [cible.section]);
  const [confirmerSuppression, setConfirmerSuppression] = useState(false);
  const compte = (espace: "apprenant" | "of") => {
    const suivies = d.pieces.filter((p) => p.espace === espace && p.suivi);
    return suivies.length ? `${suivies.filter((p) => p.statut === "valide").length}/${suivies.length}` : undefined;
  };
  const supprimer = useMutation({
    mutationFn: () => api.suppr(`/dossiers/${d.id}`),
    onSuccess: async () => {
      setConfirmerSuppression(false);
      await requetes.invalidateQueries({ queryKey: ["dossiers"] });
      navigate({ to: acteur.role === "formateur" ? "/dossiers" : "/" });
    },
    onError: (e) => notifier("danger", e.message),
  });
  const recreer = useMutation({ mutationFn: () => api.post<{ id: string }>(`/dossiers/${d.id}/recreer`), onSuccess: (n) => navigate({ to: "/dossiers/$id", params: { id: n.id } }), onError: (e) => notifier("danger", e.message) });

  return (
    <>
      <EnTete d={d}>
        <Actions d={d} />
        {d.sous_statut === "refus_financement" && acteur.role === "formateur" && (
          <Bouton variante="primaire" enCours={recreer.isPending} onClick={() => recreer.mutate()}>
            Créer un nouveau dossier à partir de celui-ci
          </Bouton>
        )}
      </EnTete>

      {d.motif_renvoi && d.sous_statut === "brouillon" && (
        <div className="mb-5">
          <Alerte ton="attention" titre="Dossier renvoyé par l'organisme pour correction">{d.motif_renvoi}</Alerte>
        </div>
      )}
      {d.motif_refus && (
        <div className="mb-5">
          <Alerte ton="danger" titre="Financement refusé">{d.motif_refus}</Alerte>
        </div>
      )}

      <Onglets
        actif={onglet}
        choisir={setOnglet}
        onglets={[
          { cle: "synthese", libelle: "Synthèse" },
          { cle: "apprenant", libelle: "Communication Apprenant", compteur: compte("apprenant") },
          { cle: "of", libelle: "Communication avec l'OF", compteur: compte("of") },
          { cle: "emargement", libelle: "Planning et émargement" },
          { cle: "journal", libelle: "Journal" },
        ]}
      />

      {onglet === "synthese" && <Synthese d={d} />}
      {onglet === "apprenant" && (
        <div className="space-y-6">
          <AccueilDuDossier d={d} />
          <EspaceCommunication d={d} espace="apprenant" />
          {d.pieces.some((p) => p.espace === null) && (
            <section>
              <h2 className="mb-1 text-base font-semibold">Hors espace de communication</h2>
              <p className="mb-3 text-sm text-encre-2">Recueil, positionnement, évaluation des acquis et enquêtes de satisfaction : renseignés et signés en ligne par l'apprenant, archivés avec le dossier. Les envois se suivent depuis l'onglet « Synthèse », sous chaque apprenant.</p>
              <AutresPieces d={d} />
            </section>
          )}
        </div>
      )}
      {onglet === "of" && (
        <div className="space-y-4">
          <EspaceCommunication d={d} espace="of" />
          {d.pieces.some((p) => p.code === "10-FIN") && acteur.role === "formateur" && (
            <p className="text-sm text-encre-2">
              Besoin d'un modèle ?{" "}
              <a href={`/api/dossiers/${d.id}/trame-facture`} target="_blank" rel="noreferrer" className="font-medium text-accent underline-offset-4 hover:underline">
                Ouvrir la trame de facture pré-remplie
              </a>{" "}
              — imprimez-la en PDF, ou déposez votre propre facture.
            </p>
          )}
        </div>
      )}
      {onglet === "emargement" && <Emargement d={d} />}
      {onglet === "journal" && <Journal d={d} />}

      {d.sous_statut === "brouillon" && d.suppression && (
        <div className="mt-10 border-t border-trait pt-5">
          {/* 04/11/2026 : même règle que le serveur (brouillon sans pièce) ; confirmation dans une boîte de dialogue. */}
          <Bouton variante="discret" icone={<Trash2 className="size-4" aria-hidden />} disabled={!d.suppression.possible} title={d.suppression.raison ?? undefined} onClick={() => setConfirmerSuppression(true)}>
            Supprimer ce brouillon
          </Bouton>
          {!d.suppression.possible && <p className="mt-1 text-[13px] text-encre-3">{d.suppression.raison}{d.suppression.details.length > 0 && ` (${d.suppression.details.join(", ")})`}</p>}
          <Modale ouverte={confirmerSuppression} fermer={() => setConfirmerSuppression(false)} titre="Supprimer ce brouillon ?">
            <p className="text-sm text-encre-2">Le dossier <strong className="text-encre">{d.dossier_reference}</strong> sera supprimé définitivement. La suppression est inscrite au journal de l'organisme.</p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Bouton variante="secondaire" onClick={() => setConfirmerSuppression(false)}>Annuler</Bouton>
              <Bouton variante="danger" icone={<Trash2 className="size-4" aria-hidden />} enCours={supprimer.isPending} onClick={() => supprimer.mutate()}>Supprimer définitivement</Bouton>
            </div>
          </Modale>
        </div>
      )}
    </>
  );
}

function Definition({ libelle, children }: { libelle: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 border-b border-trait py-2.5 last:border-0 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-4">
      <dt className="text-[13px] text-encre-3">{libelle}</dt>
      <dd className="min-w-0 text-sm whitespace-pre-line">{children || "—"}</dd>
    </div>
  );
}

function Synthese({ d }: { d: Dossier }) {
  const acteur = useActeur();
  const modifiable = !d.archive && ((acteur.role === "formateur" && d.sous_statut === "brouillon") || (acteur.role === "admin" && ["brouillon", "en_cours_validation"].includes(d.sous_statut)));
  const f = d.formation;
  const fin = d.finances;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="min-w-0 space-y-6">
        {d.manques_soumission.length > 0 && <ListeManques titre="À compléter avant de demander la validation :" manques={d.manques_soumission} />}
        {modifiable ? <EditionDossier d={d} /> : (
          <Carte className="px-5 py-2">
            <dl>
              <Definition libelle="Objectifs">{f.formation_objectifs}</Definition>
              <Definition libelle="Programme (annexe)">{f.formation_programme || "— à renseigner"}</Definition>
              <Definition libelle="Niveau · prérequis">{[f.formation_niveau, f.formation_prerequis].filter(Boolean).join(" · ")}</Definition>
              <Definition libelle="Durée">{`${heuresFr(f.formation_duree_heures_total)} sur ${f.formation_duree_jours ?? "—"} jour(s)`}</Definition>
              <Definition libelle="Modalité">{MODALITES[f.formation_modalite]}</Definition>
              <Definition libelle="Lieu">{[f.formation_lieu_nom, f.formation_lieu_adresse, f.formation_lien_visio].filter(Boolean).join("\n")}</Definition>
              <Definition libelle="Financement">{`${libelleFinancement(d.mode_financement)}${f.formation_opco ? ` — ${f.formation_opco}` : ""}`}</Definition>
              <Definition libelle="Signature de la convention">{f.signature_lieu}</Definition>
            </dl>
          </Carte>
        )}
        <Stagiaires d={d} />
        {d.cloture && <CloturePedagogique d={d} c={d.cloture} />}
        {!d.archive && ["formation_debutee", "fin_dossier_incomplet", "fin_dossier_complet"].includes(d.sous_statut) && <ObjectifsAtteints d={d} />}
      </div>

      <div className="min-w-0 space-y-6">
        <ChecklistPaiement d={d} />
        {d.demande_cpf && !d.archive && <TransmissionCpf d={d} />}
        {acteur.role !== "apprenant" && <CarteFinancementsDossier dossierId={d.id} admin={acteur.role === "admin"} />}
        <Carte id="section-parties" className="scroll-mt-20 p-5">
          <h2 className="text-base font-semibold">Parties</h2>
          <dl className="mt-2">
            <Definition libelle="Entreprise">{`${d.entreprise.entreprise_nom}\n${d.entreprise.entreprise_adresse}\nSIRET ${d.entreprise.entreprise_siret || "—"}`}</Definition>
            <Definition libelle="Représentant">{`${d.entreprise.entreprise_representant_prenom} ${d.entreprise.entreprise_representant_nom}\n${d.entreprise.entreprise_representant_email}`}</Definition>
            <Definition libelle="Formateur">
              <span className="flex items-center gap-3">
                <Avatar
                  nom={`${d.formateur.prenom} ${d.formateur.nom}`}
                  photo={d.formateur.photo}
                  taille="md"
                />
                <span className="min-w-0">{`${d.formateur.prenom} ${d.formateur.nom}\n${d.formateur.email}`}</span>
              </span>
            </Definition>
          </dl>
        </Carte>
        {fin && (
          <Carte className="p-5">
            <h2 className="text-base font-semibold">Finances</h2>
            <p className="mt-0.5 text-[13px] text-encre-3">Calculées, jamais saisies : prix de vente − commission de portage = net formateur.</p>
            <dl className="chiffres mt-3 space-y-2 text-sm">
              {[
                [`Prix unitaire HT × ${fin.formation_nb_stagiaires} stagiaire(s)`, euros(f.formation_prix_unitaire_ht)],
                ["Prix de vente HT", euros(fin.formation_prix_total_ht)],
                [`Commission de portage (${fin.portage_commission_pourcentage ?? "—"} %)`, `− ${euros(fin.portage_commission_montant)}`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="text-encre-2">{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
              <div className="flex justify-between gap-4 border-t border-trait pt-2.5 font-semibold">
                <dt>Net reversé au formateur</dt>
                <dd>{euros(fin.formateur_montant_total)}</dd>
              </div>
              <div className="flex justify-between gap-4 text-[13px] text-encre-3">
                <dt>Soit, par heure de formation (calculé)</dt>
                <dd>{euros(fin.formateur_cout_horaire)}</dd>
              </div>
            </dl>
          </Carte>
        )}
      </div>
    </div>
  );
}

/**
 * Lot 1.3 — « Avant la demande de paiement » : chaque condition, cochée ou non, apprenant par apprenant. L'état est
 * dit en toutes lettres (« acquis » / « manquant »), pas seulement par la couleur ou l'icône (WCAG 1.4.1).
 */
function ChecklistPaiement({ d }: { d: Dossier }) {
  const acteur = useActeur();
  const liste = d.controles_paiement;
  if (liste.length === 0) return null;
  const acquis = liste.filter((x) => x.acquis).length;
  const complet = acquis === liste.length;
  return (
    <Carte className={cx("p-5", !complet && "border-attente/60")}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold">Avant la demande de paiement</h2>
        <span className="chiffres shrink-0 text-sm font-semibold text-encre-2">
          {acquis}/{liste.length}
        </span>
      </div>
      <p className="mt-0.5 text-[13px] text-encre-2">{complet ? "Tout est réuni : l'organisme peut émettre la demande de paiement." : `${liste.length - acquis === 1 ? "Il manque encore 1 élément" : `Il manque encore ${liste.length - acquis} éléments`} avant que l'organisme puisse émettre la demande de paiement.`}</p>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-papier-3" aria-hidden>
        <div className={cx("h-full rounded-full", complet ? "bg-valide" : "bg-attente")} style={{ width: `${Math.round((acquis / liste.length) * 100)}%` }} />
      </div>
      <ul className="mt-3 space-y-1.5 text-sm">
        {[...liste].sort((a, b) => Number(a.acquis) - Number(b.acquis)).map((x) => (
          <li key={`${x.cle}-${x.stagiaire_id ?? ""}`} className="flex items-start gap-2">
            {x.acquis ? <Check className="mt-0.5 size-4 shrink-0 text-valide" strokeWidth={3} aria-hidden /> : <span className="mt-1 size-3.5 shrink-0 rounded-full border-2 border-attente" aria-hidden />}
            <span className={cx("min-w-0", x.acquis && "text-encre-2")}>
              {x.libelle}
              <span className="sr-only">{x.acquis ? " : acquis" : " : manquant"}</span>
              {!x.acquis && <span className="ml-1.5 text-[12px] font-semibold text-attente-encre" aria-hidden>manquant</span>}
            </span>
          </li>
        ))}
      </ul>
      {acteur.role === "formateur" && liste.some((x) => x.cle === "10-FIN" && !x.acquis) && (
        <p className="mt-3 text-[13px] text-encre-2">Votre facture se dépose dans l'onglet « Communication avec l'OF » (une trame pré-remplie y est proposée), dès que la formation est déclarée terminée.</p>
      )}
    </Carte>
  );
}

/**
 * Une section repliable de la fiche dossier (inventaire §7.3 prio 2) : un `<details>` natif (clavier et lecteur
 * d'écran gérés par le navigateur), avec le résumé de son contenu visible quand elle est repliée.
 */
function SectionDossier({ cle, titre, resume, ouvert = false, enErreur = false, children }: { cle: keyof typeof CHAMPS_SECTIONS; titre: string; resume: string; ouvert?: boolean; enErreur?: boolean; children: React.ReactNode }) {
  // Revue I-1 : l'ouverture initiale est FIGÉE au premier rendu. Dériver `open` des valeurs saisies refermait la section
  // pendant la frappe (prix, date de début, ajout d'une séance) ; ensuite, seul l'utilisateur ouvre ou ferme.
  // 04/11/2026 : la section visée par un « élément manquant » (`?section=lieu`) s'ouvre aussi.
  const cible = useSearch({ from: "/app/dossiers/$id" }).section;
  const [ouvertInitial] = useState(ouvert || cible === cle);
  const ref = useRef<HTMLDetailsElement>(null);
  // Un champ en erreur ne doit jamais rester caché dans une section repliée.
  useEffect(() => {
    if (enErreur && ref.current) ref.current.open = true;
  }, [enErreur]);
  return (
    <details ref={ref} id={`section-${cle}`} open={ouvertInitial} className={cx("group scroll-mt-20 rounded-md border open:border-trait-fort", enErreur ? "border-danger" : "border-trait")}>
      <summary className="flex min-h-12 cursor-pointer list-none items-center gap-3 px-4 py-2.5 hover:bg-papier-2 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-4 shrink-0 text-encre-3 transition-transform duration-150 group-open:rotate-90" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold">{titre}</span>
          <span className="block truncate text-[13px] text-encre-3 group-open:hidden">{resume || "à compléter"}</span>
        </span>
      </summary>
      <div className="space-y-4 border-t border-trait px-4 pt-4 pb-5">{children}</div>
    </details>
  );
}

/** Champs du formulaire, rangés par section : une erreur du serveur ouvre la section qui contient le champ fautif. */
const CHAMPS_SECTIONS = {
  formation: ["formation_titre", "formation_niveau", "formation_modalite", "formation_objectifs", "formation_programme", "formation_public_vise", "formation_prerequis"],
  lieu: ["formation_date_debut", "formation_date_fin", "formation_duree_heures_total", "formation_duree_jours", "formation_duree_heures_presentiel", "formation_duree_heures_distanciel", "formation_lieu_siret", "formation_lieu_nom", "formation_lieu_adresse", "formation_lien_visio"],
  financement: ["mode_financement", "formation_opco", "formation_prix_unitaire_ht", "signature_lieu"],
  planning: ["seances"],
} as const;

function EditionDossier({ d }: { d: Dossier }) {
  const rafraichir = useRafraichirDossier(d.id);
  const notifier = useNotifier();
  const f = d.formation;
  const [v, setV] = useState({
    formation_date_debut: f.formation_date_debut,
    formation_date_fin: f.formation_date_fin,
    formation_duree_heures_total: f.formation_duree_heures_total ?? "",
    formation_duree_jours: f.formation_duree_jours ?? "",
    formation_lieu_nom: f.formation_lieu_nom,
    formation_lieu_adresse: f.formation_lieu_adresse,
    formation_lien_visio: f.formation_lien_visio,
    formation_opco: f.formation_opco,
    prix: f.formation_prix_unitaire_ht === null ? "" : String(f.formation_prix_unitaire_ht / 100),
    signature_lieu: f.signature_lieu,
    formation_objectifs: f.formation_objectifs,
    formation_programme: f.formation_programme,
    // « Modification 1 » : tous les champs de la convention, dont la partie financière, modifiables jusqu'à la validation.
    formation_titre: f.formation_titre,
    formation_niveau: f.formation_niveau,
    formation_prerequis: f.formation_prerequis,
    formation_public_vise: f.formation_public_vise,
    formation_modalite: f.formation_modalite,
    formation_lieu_siret: f.formation_lieu_siret,
    heures_presentiel: f.formation_duree_heures_presentiel ?? "",
    heures_distanciel: f.formation_duree_heures_distanciel ?? "",
    mode_financement: d.mode_financement,
  });
  const [seances, setSeances] = useState(d.seances.map((s) => ({ date: s.date, heure_debut: s.heure_debut, heure_fin: s.heure_fin })));
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const ALIAS_ERREUR: Partial<Record<keyof typeof v, string>> = { prix: "formation_prix_unitaire_ht", heures_presentiel: "formation_duree_heures_presentiel", heures_distanciel: "formation_duree_heures_distanciel" };
  const erreurDe = (cle: keyof typeof v) => erreurs[ALIAS_ERREUR[cle] ?? cle];
  const champ = (cle: keyof typeof v) => ({ value: v[cle], erreur: erreurDe(cle), onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV({ ...v, [cle]: e.target.value }) });
  const enErreur = (section: keyof typeof CHAMPS_SECTIONS) => CHAMPS_SECTIONS[section].some((c) => Object.keys(erreurs).some((k) => k === c || k.startsWith(`${c}.`)));
  const nombre = (x: string | number) => (x === "" ? null : Number(String(x).replace(",", ".")));

  const enregistrer = useMutation({
    mutationFn: async () => {
      const { prix, heures_presentiel, heures_distanciel, ...reste } = v;
      await api.patch(`/dossiers/${d.id}`, {
        ...reste,
        formation_duree_heures_total: nombre(v.formation_duree_heures_total),
        formation_duree_jours: nombre(v.formation_duree_jours),
        formation_prix_unitaire_ht: prix === "" ? null : Math.round(nombre(prix)! * 100),
        formation_duree_heures_presentiel: v.formation_modalite === "presentiel" ? nombre(v.formation_duree_heures_total) : v.formation_modalite === "distanciel" ? null : nombre(heures_presentiel),
        formation_duree_heures_distanciel: v.formation_modalite === "distanciel" ? nombre(v.formation_duree_heures_total) : v.formation_modalite === "presentiel" ? null : nombre(heures_distanciel),
      });
      await api.put(`/dossiers/${d.id}/seances`, { seances: seances.filter((s) => s.date) });
    },
    onMutate: () => setErreurs({}),
    onSuccess: async () => {
      await rafraichir();
      notifier("succes", "Dossier enregistré.");
    },
    onError: (e) => {
      const champs = e instanceof ErreurApi ? e.details?.champs : undefined;
      if (champs) setErreurs(champs);
      notifier("danger", e.message);
    },
  });

  return (
    <Carte className="p-5">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); enregistrer.mutate(); }}>
        <div className="pb-1">
          <h2 className="text-base font-semibold">Le dossier</h2>
          <p className="mt-0.5 text-[13px] text-encre-3">Pré-rempli depuis votre catalogue et la fiche de l'entreprise. Ce que vous modifiez ici ne change que ce dossier.</p>
        </div>
        <SectionDossier cle="formation" titre="Formation" enErreur={enErreur("formation")} resume={[v.formation_titre, v.formation_niveau, LISTE_MODALITES.find((m) => m.valeur === v.formation_modalite)?.libelle].filter(Boolean).join(" · ")}>
        <Champ libelle="Intitulé (figure sur la convention)" {...champ("formation_titre")} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ListeOuAutre libelle="Niveau" options={NIVEAUX} value={v.formation_niveau} onChange={(x) => setV({ ...v, formation_niveau: x })} />
          <Selecteur libelle="Modalité" value={v.formation_modalite} onChange={(e) => setV({ ...v, formation_modalite: e.target.value as typeof v.formation_modalite })}>
            {LISTE_MODALITES.map((m) => <option key={m.valeur} value={m.valeur}>{m.libelle}</option>)}
          </Selecteur>
        </div>
        <ZoneTexte libelle="Objectifs de la formation" rows={3} {...champ("formation_objectifs")} />
        <ZoneTexte libelle="Programme détaillé" rows={4} aide="Repris de la formation ; il devient l'annexe « Programme de formation » de la convention." {...champ("formation_programme")} />
        <ZoneTexte libelle="Public visé" rows={2} {...champ("formation_public_vise")} />
        <ZoneTexte libelle="Prérequis" rows={2} {...champ("formation_prerequis")} />
        </SectionDossier>
        <SectionDossier cle="lieu" titre="Dates, durée et lieu" resume={[v.formation_date_debut ? `du ${dateFr(v.formation_date_debut)} au ${dateFr(v.formation_date_fin)}` : "dates à définir", v.formation_duree_heures_total !== "" ? `${String(v.formation_duree_heures_total).replace(".", ",")} h` : "", v.formation_lieu_nom].filter(Boolean).join(" · ")} ouvert={!v.formation_date_debut} enErreur={enErreur("lieu")}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Champ libelle="Date de début" type="date" {...champ("formation_date_debut")} />
          <Champ libelle="Date de fin" type="date" min={v.formation_date_debut} {...champ("formation_date_fin")} />
          <Champ libelle="Durée totale (heures)" inputMode="decimal" {...champ("formation_duree_heures_total")} />
          <Champ libelle="Nombre de jours" inputMode="decimal" {...champ("formation_duree_jours")} />
        </div>
        {v.formation_modalite === "mixte" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Champ libelle="Dont heures en présentiel" inputMode="decimal" {...champ("heures_presentiel")} />
            <Champ libelle="Dont heures à distance" inputMode="decimal" {...champ("heures_distanciel")} />
          </div>
        )}
        {v.formation_modalite !== "distanciel" && (
          <div className="space-y-4">
            <ChampSiret libelle="SIRET du lieu" erreur={erreurs.formation_lieu_siret} aide="Facultatif. Tapez le SIRET ou le nom : nom et adresse du lieu se remplissent s'ils sont vides." value={v.formation_lieu_siret} onChange={(x) => setV((y) => ({ ...y, formation_lieu_siret: x }))} onChoisir={(e) => setV((y) => ({ ...y, formation_lieu_siret: e.siret, formation_lieu_nom: y.formation_lieu_nom || e.nom_commercial || e.raison_sociale, formation_lieu_adresse: y.formation_lieu_adresse || e.adresse }))} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <Champ libelle="Lieu de formation" {...champ("formation_lieu_nom")} />
            <Champ libelle="Adresse du lieu" {...champ("formation_lieu_adresse")} />
            </div>
          </div>
        )}
        {v.formation_modalite !== "presentiel" && <Champ libelle="Lien de connexion à distance" type="url" placeholder="https://…" {...champ("formation_lien_visio")} />}
        </SectionDossier>
        <SectionDossier cle="financement" titre="Financement" resume={[libelleMode(v.mode_financement), avecOpco(v.mode_financement) ? v.formation_opco : "", v.prix !== "" ? `${v.prix.replace(".", ",")} € HT / stagiaire` : "prix à renseigner"].filter(Boolean).join(" · ")} ouvert={v.prix === ""} enErreur={enErreur("financement")}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Selecteur libelle="Mode de financement" value={v.mode_financement} onChange={(e) => { const m = e.target.value as typeof v.mode_financement; setV({ ...v, mode_financement: m, formation_opco: avecOpco(m) ? v.formation_opco : "" }); }}>
              {choixModes(d.mode_financement).map((m) => <option key={m.valeur} value={m.valeur}>{m.libelle}</option>)}
            </Selecteur>
            {/* Second menu seulement pour un OPCO : CPF, FAFCEA et FIFPL désignent déjà le financeur (04/11/2026). */}
            {avecOpco(v.mode_financement) && <ListeOuAutre libelle="OPCO" options={FINANCEURS} value={v.formation_opco} onChange={(x) => setV({ ...v, formation_opco: x })} />}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Champ libelle="Prix unitaire HT (€)" inputMode="decimal" aide="Par stagiaire." {...champ("prix")} />
            <Champ libelle="Convention signée à" placeholder="Ville" {...champ("signature_lieu")} />
          </div>
          {(() => {
            // Estimation en direct, non modifiable (04/11/2026) : prix saisi × stagiaires × (100 − commission de portage) %.
            const pct = d.finances?.portage_commission_pourcentage;
            const nb = d.finances?.formation_nb_stagiaires ?? 0;
            const prix = Number(v.prix.replace(",", ".").replace(/\s/g, ""));
            if (pct == null || !nb || !Number.isFinite(prix) || prix <= 0) return <p className="text-[12.5px] text-encre-3">La rémunération du formateur n'est pas saisie : elle se calcule automatiquement dès que le prix et les stagiaires sont renseignés.</p>;
            const part = 100 - Number(pct);
            const net = Math.round(prix * nb * part) / 100;
            return (
              <div className="rounded-lg border border-trait bg-papier-2 px-4 py-3 text-sm" aria-live="polite">
                <div className="flex justify-between gap-4 font-semibold"><span>Rémunération du formateur (calculée)</span><span className="chiffres">{net.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span></div>
                <p className="mt-1 text-[12.5px] text-encre-3">{`${prix.toLocaleString("fr-FR")} € HT × ${nb} stagiaire(s) × ${part} % (commission de portage de ${pct} %). Non modifiable.`}</p>
              </div>
            );
          })()}
        </SectionDossier>

        <SectionDossier cle="planning" titre="Planning des séances" resume={seances.filter((x) => x.date).length === 0 ? "aucune séance" : `${seances.filter((x) => x.date).length} séance(s)`} ouvert={seances.length === 0} enErreur={enErreur("planning")}>
          <div className="space-y-2">
            {seances.map((s, i) => (
              // Téléphone : la date sur sa ligne, les deux heures dessous (en une seule ligne, dates et heures étaient rognées).
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2 border-b border-trait pb-2 last:border-0 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:border-0 sm:pb-0">
                <input type="date" aria-label={`Date de la séance ${i + 1}`} value={s.date} min={v.formation_date_debut} onChange={(e) => setSeances(seances.map((x, k) => (k === i ? { ...x, date: e.target.value } : x)))} className={cx(CHAMP_SEANCE, "order-1 col-span-2 sm:col-span-1")} />
                <input type="time" aria-label={`Heure de début de la séance ${i + 1}`} value={s.heure_debut} onChange={(e) => setSeances(seances.map((x, k) => (k === i ? { ...x, heure_debut: e.target.value } : x)))} className={cx(CHAMP_SEANCE, "order-3 sm:order-2")} />
                <input type="time" aria-label={`Heure de fin de la séance ${i + 1}`} value={s.heure_fin} onChange={(e) => setSeances(seances.map((x, k) => (k === i ? { ...x, heure_fin: e.target.value } : x)))} className={cx(CHAMP_SEANCE, "order-4 sm:order-3")} />
                <Bouton variante="discret" taille="sm" className="order-2 sm:order-4" aria-label={`Retirer la séance ${i + 1}`} icone={<Trash2 className="size-4" aria-hidden />} onClick={() => setSeances(seances.filter((_, k) => k !== i))} />
              </div>
            ))}
          </div>
          <Bouton
            variante="discret"
            taille="sm"
            className="mt-2"
            disabled={seances.length >= 20}
            icone={<Plus className="size-4" aria-hidden />}
            onClick={() => setSeances([...seances, { date: seances.at(-1)?.date ?? v.formation_date_debut, heure_debut: seances.at(-1)?.heure_debut === "09:00" ? "13:30" : "09:00", heure_fin: seances.at(-1)?.heure_debut === "09:00" ? "17:00" : "12:30" }])}
          >
            Ajouter une séance
          </Bouton>
        </SectionDossier>

        {/* Barre collante : « Enregistrer » reste à portée, quelle que soit la section ouverte. */}
        <div className="sticky bottom-0 z-10 -mx-5 -mb-5 flex flex-wrap items-center justify-between gap-3 rounded-b-md border-t border-trait bg-carte/95 px-5 py-3 backdrop-blur">
          <p className="text-[13px] text-encre-3">Les modifications ne valent que pour ce dossier.</p>
          <Bouton type="submit" variante="primaire" enCours={enregistrer.isPending}>
            Enregistrer le dossier
          </Bouton>
        </div>
      </form>
    </Carte>
  );
}

function Stagiaires({ d }: { d: Dossier }) {
  const rafraichir = useRafraichirDossier(d.id);
  const notifier = useNotifier();
  const inviter = useMutation({
    mutationFn: (stagiaire_id: string) => api.post<{ lien: string }>(`/dossiers/${d.id}/inviter`, { stagiaire_id }),
    onSuccess: async (r) => {
      await rafraichir();
      await navigator.clipboard?.writeText(r.lien).catch(() => undefined);
      notifier("succes", "Invitation envoyée. Le lien est aussi copié dans le presse-papiers.");
    },
    onError: (e) => notifier("danger", e.message),
  });
  const relancer = useMutation({
    mutationFn: (stagiaire_id: string) => api.post<{ pieces: string[] }>(`/dossiers/${d.id}/relancer`, { stagiaire_id }),
    onSuccess: async (r) => {
      await rafraichir();
      notifier("succes", `Relance envoyée : ${r.pieces.join(", ")}.`);
    },
    onError: (e) => notifier("danger", e.message),
  });

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold">Apprenants</h2>
      <div className="space-y-4">
        {d.stagiaires.map((st) => (
          <Carte key={st.id} className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
              <div className="flex min-w-0 items-start gap-3">
                <Avatar nom={`${st.prenom} ${st.nom}`} photo={st.photo} taille="md" />
                <div className="min-w-0">
                  <p className="font-medium">
                    {st.prenom} {st.nom}
                  </p>
                  <p className="truncate text-[13px] text-encre-2">
                    {[st.poste, st.email].filter(Boolean).join(" · ")}
                  </p>
                  {/* Où en est cet apprenant dans son parcours : la même lecture que dans son propre espace. */}
                  <ol className="mt-2 flex flex-wrap gap-1" aria-label={`Parcours de ${st.prenom}`}>
                    {(d.parcours.find((p) => p.stagiaire_id === st.id)?.sections ?? []).map((sec) => (
                      <li
                        key={sec.cle}
                        title={sec.message}
                        className={cx(
                          "rounded-xs px-1.5 py-0.5 text-[10px] font-semibold",
                          STYLE_SECTION[sec.etat].pastille,
                        )}
                      >
                        {sec.titre.split(" — ")[0]}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
              {!d.archive && (
                <div className="flex flex-wrap gap-1.5">
                  <Bouton variante="secondaire" taille="sm" icone={<MailPlus className="size-3.5" aria-hidden />} enCours={inviter.isPending && inviter.variables === st.id} onClick={() => inviter.mutate(st.id)}>
                    {st.a_un_compte ? "Renvoyer l'invitation" : "Inviter"}
                  </Bouton>
                  <Bouton variante="discret" taille="sm" icone={<BellRing className="size-3.5" aria-hidden />} enCours={relancer.isPending && relancer.variables === st.id} onClick={() => relancer.mutate(st.id)}>
                    Relancer
                  </Bouton>
                </div>
              )}
            </div>
            <div className="border-t border-trait bg-papier-2/60">
              <FormulairesApprenant d={d} stagiaireId={st.id} />
            </div>
          </Carte>
        ))}
      </div>
    </section>
  );
}

/**
 * Lot 2.5 — Clôture pédagogique : par défaut, tout vient de l'émargement ; le formateur ne corrige que l'écart, avec un
 * motif. Les valeurs retenues alimentent l'attestation de réalisation et le BPF.
 */
function CloturePedagogique({ d, c }: { d: Dossier; c: NonNullable<Dossier["cloture"]> }) {
  const rafraichir = useRafraichirDossier(d.id);
  const notifier = useNotifier();
  const enSaisie = (h: number) => String(h).replace(".", ",");
  const [dispensees, setDispensees] = useState(enSaisie(c.heures_dispensees));
  const [lignes, setLignes] = useState(c.stagiaires.map((x) => ({ stagiaire_id: x.stagiaire_id, heures: enSaisie(x.heures_retenues), assiduite: x.assiduite })));
  const [motif, setMotif] = useState("");
  const nombre = (x: string) => Number(x.replace(",", "."));
  const ecart = Math.abs(nombre(dispensees) - c.heures_prevues) > 0.001 || lignes.some((l, i) => Math.abs(nombre(l.heures) - c.stagiaires[i]!.heures_emargees) > 0.001);
  const enregistrer = useMutation({
    mutationFn: () => api.put(`/dossiers/${d.id}/cloture`, { heures_dispensees: nombre(dispensees), stagiaires: lignes.map((l) => ({ ...l, heures: nombre(l.heures) })), motif }),
    onSuccess: async () => {
      await rafraichir();
      setMotif("");
      notifier("succes", "Clôture enregistrée : l'attestation et le BPF reprennent ces heures.");
    },
  });
  const erreurs = enregistrer.error instanceof ErreurApi && Array.isArray(enregistrer.error.details?.erreurs) ? enregistrer.error.details.erreurs : [];
  return (
    <Carte className="p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Clôture pédagogique</h2>
        <span className="text-[12.5px] text-encre-3">{c.declaree ? `Déclarée le ${instantFr(c.cloture_le)}` : "Pré-remplie depuis l'émargement"}</span>
      </div>
      <p className="mt-0.5 text-[13px] text-encre-2">Heures dispensées, heures réalisées et assiduité de chaque apprenant : elles figurent sur l'attestation de réalisation (ind. 11) et dans le BPF. Ne corrigez que ce qui diffère de l'émargement.</p>
      {c.ouverte ? (
        <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); enregistrer.mutate(); }}>
          <Champ libelle="Heures réellement dispensées" inputMode="decimal" aide={`Planning : ${heuresFr(c.heures_prevues)}.`} value={dispensees} onChange={(e) => setDispensees(e.target.value)} className="max-w-40" />
          <ul className="divide-y divide-trait rounded-md border border-trait">
            {c.stagiaires.map((x, i) => (
              <li key={x.stagiaire_id} className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-[minmax(0,1fr)_110px_minmax(0,220px)] sm:items-end">
                <div className="min-w-0 text-sm">
                  <span className="font-medium">{x.nom}</span>
                  <span className="block text-[12.5px] text-encre-3">Émargement : {heuresFr(x.heures_emargees)}</span>
                </div>
                <Champ libelle="Heures réalisées" inputMode="decimal" value={lignes[i]!.heures} onChange={(e) => setLignes(lignes.map((l, k) => (k === i ? { ...l, heures: e.target.value } : l)))} />
                <Selecteur libelle="Assiduité" value={lignes[i]!.assiduite} onChange={(e) => setLignes(lignes.map((l, k) => (k === i ? { ...l, assiduite: e.target.value } : l)))}>
                  {ASSIDUITES.map((a) => <option key={a.valeur} value={a.valeur}>{a.libelle}</option>)}
                </Selecteur>
              </li>
            ))}
          </ul>
          {ecart && <ZoneTexte libelle="Motif de l'écart (obligatoire)" rows={2} required aide="Gardé au journal du dossier : séance écourtée, feuille papier, abandon…" value={motif} onChange={(e) => setMotif(e.target.value)} />}
          {enregistrer.error && <Alerte ton="danger" titre={enregistrer.error.message}>{erreurs.length > 1 && <ul className="list-disc pl-4">{erreurs.slice(1).map((x) => <li key={x}>{x}</li>)}</ul>}</Alerte>}
          <Bouton type="submit" taille="sm" variante="primaire" enCours={enregistrer.isPending}>
            Enregistrer la clôture
          </Bouton>
        </form>
      ) : (
        <dl className="chiffres mt-3 space-y-1 text-sm">
          <div className="flex justify-between gap-3"><dt className="text-encre-2">Heures dispensées</dt><dd>{heuresFr(c.heures_dispensees)}</dd></div>
          {c.stagiaires.map((x) => (
            <div key={x.stagiaire_id} className="flex justify-between gap-3"><dt className="text-encre-2">{x.nom}</dt><dd>{heuresFr(x.heures_retenues)} · {ASSIDUITES.find((a) => a.valeur === x.assiduite)?.libelle ?? x.assiduite}</dd></div>
          ))}
        </dl>
      )}
    </Carte>
  );
}

function ObjectifsAtteints({ d }: { d: Dossier }) {
  const rafraichir = useRafraichirDossier(d.id);
  const notifier = useNotifier();
  const [texte, setTexte] = useState(d.formation.formation_objectifs_atteints);
  const enregistrer = useMutation({ mutationFn: () => api.patch(`/dossiers/${d.id}/objectifs-atteints`, { texte }), onSuccess: async () => { await rafraichir(); notifier("succes", "Enregistré. Pensez à régénérer l'attestation si elle n'est pas encore signée."); } });
  return (
    <Carte className="p-5">
      <ZoneTexte libelle="Objectifs atteints" aide="Figure sur l'attestation de réalisation (09-FIN)." value={texte} onChange={(e) => setTexte(e.target.value)} />
      <Bouton className="mt-3" taille="sm" enCours={enregistrer.isPending} onClick={() => enregistrer.mutate()}>
        Enregistrer
      </Bouton>
    </Carte>
  );
}

// ——— Planning et émargement ———
function Emargement({ d }: { d: Dossier }) {
  const acteur = useActeur();
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const etat = useQuery({ queryKey: ["emargement", d.id], queryFn: () => api.get<SeanceEmargement[]>(`/dossiers/${d.id}/emargement`), ...suiviEnDirect });
  const [seance, setSeance] = useState<SeanceEmargement | null>(null);
  const [trace, setTrace] = useState<string | null>(null);
  const ouvert = ["formation_debutee", "fin_dossier_incomplet", "fin_dossier_complet"].includes(d.sous_statut);
  const emarger = useMutation({
    mutationFn: () => api.post(`/seances/${seance!.id}/emarger`, { trace_png: trace }),
    onSuccess: async () => {
      await Promise.all([requetes.invalidateQueries({ queryKey: ["emargement", d.id] }), requetes.invalidateQueries({ queryKey: ["dossier", d.id] })]);
      setSeance(null);
      setTrace(null);
      notifier("succes", "Émargement enregistré.");
    },
  });
  if (etat.isPending) return <Chargement />;
  if (!etat.data || etat.data.length === 0) return <Alerte>Aucune séance n'est encore planifiée.</Alerte>;
  const moi = acteur.role === "apprenant" ? acteur.stagiaire_id : null;

  return (
    <>
      {!ouvert && !d.archive && <div className="mb-4"><Alerte>L'émargement s'ouvrira au démarrage de la formation.</Alerte></div>}
      <Carte className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-trait text-left text-xs tracking-wide text-encre-3 uppercase">
              <th className="px-4 py-3 font-semibold">Séance</th>
              <th className="px-4 py-3 font-semibold">Horaires</th>
              {d.stagiaires.map((st) => (
                <th key={st.id} className="px-4 py-3 font-semibold">{st.prenom}</th>
              ))}
              <th className="px-4 py-3 font-semibold">Formateur</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-trait">
            {etat.data.map((se) => {
              const signe = (stagiaire_id: string, qui: "stagiaire" | "formateur") => se.signatures.some((x) => x.stagiaire_id === stagiaire_id && x.signataire === qui);
              const resteAMoi = moi ? !signe(moi, "stagiaire") : acteur.role === "formateur" && d.stagiaires.some((st) => !signe(st.id, "formateur"));
              return (
                <tr key={se.id}>
                  <td className="chiffres px-4 py-3 font-medium whitespace-nowrap">{dateLongue(se.date)}</td>
                  <td className="chiffres px-4 py-3 whitespace-nowrap text-encre-2">
                    {se.heure_debut} – {se.heure_fin} · {heuresFr(se.duree_heures)}
                  </td>
                  {d.stagiaires.map((st) => (
                    <td key={st.id} className="px-4 py-3">{signe(st.id, "stagiaire") ? <Check className="size-4 text-valide" aria-label="Signé" /> : <span className="text-encre-3">—</span>}</td>
                  ))}
                  <td className="px-4 py-3">{d.stagiaires.every((st) => signe(st.id, "formateur")) ? <Check className="size-4 text-valide" aria-label="Contresigné" /> : <span className="text-encre-3">—</span>}</td>
                  <td className="px-4 py-2 text-right">
                    {ouvert && resteAMoi && (
                      <Bouton variante="secondaire" taille="sm" icone={<PenLine className="size-3.5" aria-hidden />} onClick={() => setSeance(se)}>
                        {moi ? "Émarger" : "Contresigner"}
                      </Bouton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Carte>
      {acteur.role !== "apprenant" && (
        <p className="chiffres mt-3 text-sm text-encre-2">Heures réalisées : {d.stagiaires.map((st) => `${st.prenom} ${heuresFr(st.heures_realisees)}`).join(" · ")}</p>
      )}

      <Modale ouverte={seance !== null} fermer={() => setSeance(null)} titre={seance ? `Émargement — ${dateLongue(seance.date)}, ${seance.heure_debut} – ${seance.heure_fin}` : ""}>
        <p className="mb-4 text-sm text-encre-2">{moi ? "Signez pour attester de votre présence à cette séance." : "Votre signature contresigne la présence de tous les stagiaires de cette séance."}</p>
        <ZoneDeTrace surChangement={setTrace} hauteur={150} />
        {emarger.error && <p className="mt-2 text-sm text-danger">{emarger.error.message}</p>}
        <Bouton variante="primaire" className="mt-3 w-full" disabled={!trace} enCours={emarger.isPending} onClick={() => emarger.mutate()}>
          Valider ma signature
        </Bouton>
      </Modale>
    </>
  );
}

function Journal({ d }: { d: Dossier }) {
  if (d.journal.length === 0) return <Alerte>Aucun événement pour l'instant.</Alerte>;
  const ROLES: Record<string, string> = { admin: "Organisme", formateur: "Formateur", apprenant: "Apprenant", systeme: "Automatique" };
  return (
    <Carte className="px-5 py-2">
      <ol>
        {d.journal.map((e) => (
          <li key={e.id} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 border-b border-trait py-3 last:border-0 sm:grid-cols-[120px_90px_minmax(0,1fr)]">
            <span className="chiffres text-[13px] text-encre-3">{instantFr(e.cree_le)}</span>
            <span className="max-sm:hidden"><Etiquette ton={e.acteur_role === "systeme" ? "neutre" : "accent"}>{ROLES[e.acteur_role] ?? e.acteur_role}</Etiquette></span>
            <span className="text-sm max-sm:col-span-2">{e.libelle}</span>
          </li>
        ))}
      </ol>
    </Carte>
  );
}

// ——— Vue de l'apprenant : son parcours, section par section (cahier des charges oral du 23/09/2026) ———
type SectionParcours = Dossier["parcours"][number]["sections"][number];

const STYLE_SECTION: Record<SectionParcours["etat"], { cadre: string; pastille: string; libelle: string }> = {
  a_faire: { cadre: "border-attente/70 bg-attente-doux/40", pastille: "bg-attente text-attente-encre", libelle: "À faire" },
  en_attente: { cadre: "border-trait bg-papier-2", pastille: "bg-papier-3 text-encre-2", libelle: "En cours" },
  termine: { cadre: "border-valide/40 bg-valide-doux/60", pastille: "bg-valide text-sur-accent", libelle: "Terminé" },
  refuse: { cadre: "border-danger/40 bg-danger-doux", pastille: "bg-danger text-sur-accent", libelle: "Refusé" },
  a_venir: { cadre: "border-dashed border-trait-fort bg-papier-2", pastille: "bg-papier-3 text-encre-3", libelle: "À venir" },
};

function DeclarationDepot({ d }: { d: Dossier }) {
  const rafraichir = useRafraichirDossier(d.id);
  const notifier = useNotifier();
  const [coche, setCoche] = useState(false);
  const action = d.actions.find((a) => a.action === "declarer_depot");
  const declarer = useMutation({
    mutationFn: () => api.post(`/dossiers/${d.id}/actions/declarer_depot`, {}),
    onSuccess: async () => {
      await rafraichir();
      notifier("succes", "Merci : votre formateur et l'organisme sont informés du dépôt de votre demande.");
    },
    onError: (e) => {
      setCoche(false);
      notifier("danger", e.message);
    },
  });
  if (!action) return null;
  const bloquee = action.bloqueePar !== null;
  return (
    <div className="mt-3 rounded-md border border-trait bg-carte p-4">
      <label className={cx("flex items-start gap-3 text-sm", bloquee ? "cursor-not-allowed text-encre-3" : "cursor-pointer")}>
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-(--color-accent)"
          checked={coche}
          disabled={bloquee || declarer.isPending}
          onChange={(e) => setCoche(e.target.checked)}
        />
        <span>
          <strong className="font-semibold">J'affirme avoir déposé la demande de financement</strong> auprès de mon organisme de financement (OPCO, FAF…), avec les documents ci-dessus.
          {bloquee && <span className="mt-1 block text-xs">{action.bloqueePar}</span>}
        </span>
      </label>
      <Bouton className="mt-3" variante="primaire" taille="sm" disabled={!coche || bloquee} enCours={declarer.isPending} onClick={() => declarer.mutate()}>
        Confirmer le dépôt de ma demande
      </Bouton>
    </div>
  );
}

function SectionApprenant({ d, section, rang, children }: { d: Dossier; section: SectionParcours; rang: number; children?: React.ReactNode }) {
  const style = STYLE_SECTION[section.etat];
  const ouverte = section.etat !== "a_venir";
  return (
    <section id={`section-${section.cle}`} aria-label={section.titre} data-etat={section.etat} className={cx("scroll-mt-4 rounded-lg border p-4 transition-[color,background-color,border-color,box-shadow] duration-300 outline-none sm:p-5 data-signale:ring-4 data-signale:ring-attente/70", style.cadre)}>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="chiffres grid size-7 shrink-0 place-items-center rounded-full bg-carte text-sm font-semibold text-encre-2 shadow-carte">{rang}</span>
        <h2 className="min-w-0 flex-1 text-base font-semibold sm:text-lg">{section.titre}</h2>
        <span className={cx("rounded-xs px-2 py-0.5 text-[11px] font-semibold tracking-wide", style.pastille)}>{style.libelle}</span>
      </header>
      <p className="mt-2 text-sm text-encre-2">{section.message}</p>
      {ouverte && section.pieces.length > 0 && (
        <div className="mt-3">
          <ListePieces d={d} codes={section.pieces} />
        </div>
      )}
      {ouverte && children}
    </section>
  );
}

function VueApprenant({ d }: { d: Dossier }) {
  const acteur = useActeur();
  const coffres = useQuery({ queryKey: ["coffres"], queryFn: () => api.get<CoffreApprenant[]>("/coffres") });
  const coffre = coffres.data?.find((c) => c.dossier_id === d.id);
  const sections = d.parcours.find((p) => p.stagiaire_id === acteur.stagiaire_id)?.sections ?? [];
  // La section « Accord » n'existe pour l'apprenant qu'une fois sa demande déclarée déposée.
  const visibles = sections.filter((s) => s.cle !== "accord" || s.etat !== "a_venir");
  const aFaire = visibles.filter((s) => s.etat === "a_faire");

  // 04/11/2026 : en-tête façon Notion (couverture, photo et message du formateur) et plan en arborescence.
  useTitreDocument(`${d.dossier_reference} · ${d.formation.formation_titre}`);
  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/" className="mb-3 inline-flex items-center gap-1.5 text-sm text-encre-3 hover:text-encre">
        <ArrowLeft className="size-4" aria-hidden /> Mes formations
      </Link>
      <div className="mb-6">
        <EnTeteApprenant d={d}>
          <p className="chiffres mt-4 flex flex-wrap items-center gap-2 text-[13px] text-encre-3">
            <span className="font-semibold tracking-wide">{d.dossier_reference}</span>
            <Etiquette ton={d.sous_statut === "refus_financement" ? "danger" : d.archive ? "neutre" : "accent"}>{d.libelle_statut}</Etiquette>
            <span>{d.formation.formation_date_debut ? `du ${dateFr(d.formation.formation_date_debut)} au ${dateFr(d.formation.formation_date_fin)}` : "dates à définir"}</span>
            {d.archive && (
              <span className="inline-flex items-center gap-1 text-xs font-medium">
                <Lock className="size-3" aria-hidden /> Lecture seule
              </span>
            )}
          </p>
          <Frise d={d} />
        </EnTeteApprenant>
      </div>
      <div className="lg:grid lg:grid-cols-[19rem_minmax(0,1fr)] lg:gap-6">
        <PlanApprenant d={d} stagiaireId={acteur.stagiaire_id!} sections={visibles.map((s) => s.cle)} coffre={coffre} />
        <div className="min-w-0">
      {!d.archive && (
        <div className="mb-6">
          {aFaire.length > 0 ? (
            <Alerte ton="attention" titre={`Prochaine étape : ${aFaire[0]!.titre}`}>{aFaire[0]!.message}</Alerte>
          ) : (
            <Alerte ton="succes" titre="Vous êtes à jour">Rien n'attend votre action pour le moment.</Alerte>
          )}
        </div>
      )}

      <ol className="mb-6 flex flex-wrap gap-1.5" aria-label="Mon parcours">
        {visibles.map((s, i) => (
          <li key={s.cle} className={cx("rounded-xs px-2 py-1 text-[11px] font-semibold", STYLE_SECTION[s.etat].pastille)}>
            {i + 1}. {s.titre.split(" — ")[0]}
          </li>
        ))}
      </ol>

      <div className="space-y-4">
        {visibles.map((section, i) => (
          <SectionApprenant key={section.cle} d={d} section={section} rang={i + 1}>
            {section.cle === "preliminaire" && (
              <div className="mt-3">
                <Questionnaires d={d} stagiaireId={acteur.stagiaire_id!} types={["recueil", "positionnement"]} />
              </div>
            )}
            {section.cle === "financement" && <DeclarationDepot d={d} />}
            {section.cle === "realisation" && (
              <div className="mt-3 space-y-4">
                <Questionnaires d={d} stagiaireId={acteur.stagiaire_id!} types={["acquis", "satisfaction_chaud", "satisfaction_froid"]} />
                {d.seances.length > 0 && <Emargement d={d} />}
              </div>
            )}
          </SectionApprenant>
        ))}
      </div>

      <section id="supports" className="mt-8 scroll-mt-4 rounded-lg outline-none data-signale:ring-4 data-signale:ring-attente/70">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
          Supports de formation
          {coffre ? <Unlock className="size-4 text-valide" aria-hidden /> : <Lock className="size-4 text-encre-3" aria-hidden />}
        </h2>
        {coffre && coffre.fichiers.length > 0 ? (
          <Carte>
            <ul className="divide-y divide-trait">
              {coffre.fichiers.map((fichier) => (
                <li key={fichier.id} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
                  <span className="min-w-0 truncate text-sm font-medium">{fichier.nom_fichier}</span>
                  <a href={`/api/coffre/${fichier.id}/telecharger`} className="inline-flex shrink-0 items-center gap-2 rounded-sm px-3 py-1.5 text-[13px] font-medium text-accent hover:bg-accent-doux">
                    <Download className="size-3.5" aria-hidden />
                    <span className="chiffres">{octets(fichier.taille)}</span>
                  </a>
                </li>
              ))}
            </ul>
          </Carte>
        ) : (
          <Alerte>{coffre ? "Votre formateur n'a pas encore partagé de support." : "Les supports seront accessibles dès que le financement de votre formation sera accordé."}</Alerte>
        )}
      </section>
        </div>
      </div>
    </div>
  );
}

